import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

/**
 * Per-user durable preference. The value lives under
 * `/auth/me/preferences/:namespace` so it follows the user across devices.
 *
 *   const { value, setValue, ready } = usePreferences('crud-cols:/fleet/assets', defaultColumns);
 *
 * - On mount: GETs the namespace; if no record yet, falls back to `defaultValue`
 * - On `setValue(...)`: optimistic update + debounced PUT (400ms)
 * - `ready` flips true once the initial GET resolves so callers can avoid
 *   flashing the default value before the server's value lands.
 */
export function usePreferences<T>(namespace: string, defaultValue: T) {
  const [value, setValueState] = useState<T>(defaultValue);
  const [ready, setReady] = useState(false);
  const writeTimer = useRef<number | null>(null);
  const latestValue = useRef<T>(defaultValue);
  // Hold the live defaultValue so the namespace-change effect always resets
  // to the *current* default, not the value captured at mount.
  const defaultRef = useRef(defaultValue);
  useEffect(() => { defaultRef.current = defaultValue; });

  useEffect(() => {
    let cancelled = false;
    // Pre-flight reset — without this, switching to a namespace that has no
    // saved value (e.g. /fleet/assets → /fleet/archived-assets, where the
    // archived report has no saved filter) carries the previous namespace's
    // state forward instead of falling back to the default. The bug shows up
    // as filters from one report bleeding into a sibling report.
    setValueState(defaultRef.current);
    latestValue.current = defaultRef.current;
    setReady(false);
    (async () => {
      try {
        const r = await api<{ namespace: string; value: T | null }>(`/auth/me/preferences/${encodeURIComponent(namespace)}`);
        if (cancelled) return;
        if (r.success && r.data && r.data.value != null) {
          setValueState(r.data.value as T);
          latestValue.current = r.data.value as T;
        }
        // If the server has no saved value for this namespace, the pre-flight
        // reset above already left us at the default — nothing more to do.
      } catch {
        /* on error keep default — already applied above */
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
    // namespace is the only meaningful trigger; defaultValue updates flow
    // through `defaultRef`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace]);

  function setValue(updater: T | ((prev: T) => T)) {
    setValueState((prev) => {
      const next = typeof updater === 'function' ? (updater as (p: T) => T)(prev) : updater;
      latestValue.current = next;
      if (writeTimer.current != null) window.clearTimeout(writeTimer.current);
      writeTimer.current = window.setTimeout(() => {
        api(`/auth/me/preferences/${encodeURIComponent(namespace)}`, {
          method: 'PUT',
          body: JSON.stringify({ value: latestValue.current }),
        }).catch(() => { /* swallow — preference is best-effort */ });
      }, 400);
      return next;
    });
  }

  return { value, setValue, ready };
}
